import _ from 'lodash'
import { createSelector } from 'reselect'
import { getVisitsForRange } from '../stateUtils'
import moment from 'moment/moment'
import { roomsSelector } from './objectSelectors'
import { parseDate } from '../../utils/dateUtils'

const visitsSelector = state => state.visits
const guestsSelector = state => state.guests

const dashboardSelector = state => state.dashboard
const dashboardPropsSelector = state => dashboardSelector(state).props

export const dashboardStartDateSelector = createSelector(
  dashboardPropsSelector,
  props => parseDate(props.startDate)
)
export const dashboardEndDateSelector = createSelector(
  dashboardPropsSelector,
  props => parseDate(props.endDate)
)
export const dashboardDialogsSelector = state =>
  dashboardSelector(state).dialogs
export const searchTextSelector = state =>
  dashboardPropsSelector(state).searchText.toLowerCase()
export const programRoomsVisibleSelector = state =>
  dashboardPropsSelector(state).programRoomsVisible

export const visitsLoadingSelector = state =>
  dashboardPropsSelector(state).isLoading

function getEventsByRoom(rooms, visits, guests, startDate, endDate) {
  let visitsByRoom = _(visits)
    .values()
    .map(visit => ({
      ...visit,
      guest: guests[visit.guest]
    }))
    .groupBy('roomId')
    .value()
  // Add entries for rooms without visits so blackout dates are added correctly
  _.forEach(rooms, room => {
    if (!_.has(visitsByRoom, room.id)) {
      visitsByRoom[room.id] = []
    }
  })
  let filteredVisitsByRoom = _.mapValues(visitsByRoom, (visits, roomId) => {
    const room = rooms[roomId]

    // Debug: detect and log missing room definitions which previously caused
    // "Cannot read properties of undefined (reading 'blackoutFrom')"
    if (!room) {
      /* eslint-disable no-console */
      if (typeof window !== 'undefined' && window.console) {
        console.warn(
          `dashboardSelectors:getEventsByRoom – room definition not found for id ${roomId}. ` +
            'This can happen if there are visits referencing a room that is not loaded or the id type (string vs number) mismatches.'
        )
      }
      /* eslint-enable no-console */

      // Even if the room is missing we still want to return visits in range
      return getVisitsForRange(visits, startDate, endDate)
    }

    if (room.blackoutFrom && room.blackoutTo) {
      visits.push({
        type: 'blackout',
        // We use check in and out dates to work with existing code that
        // was written for visit objects. This should probably be changed
        // if we add other events.
        checkInDate: moment(room.blackoutFrom),
        checkOutDate: moment(room.blackoutTo)
      })
    }

    return getVisitsForRange(visits, startDate, endDate)
  })

  // Separate visits into levels so that overlapping visits are displayed one under the other
  let visitsWithLevels = _.mapValues(filteredVisitsByRoom, (visits, roomId) => {
    let sortedVisits = _.sortBy(visits, 'checkInDate')
    let lastVisitForLevel = []
    sortedVisits.forEach(visit => {
      let freeLevelIndex = _.findIndex(lastVisitForLevel, visitForLevel =>
        moment(visit.checkInDate).isAfter(visitForLevel.checkOutDate)
      )
      if (freeLevelIndex >= 0) {
        visit.level = freeLevelIndex
        lastVisitForLevel[freeLevelIndex] = visit
      } else {
        visit.level = lastVisitForLevel.length
        lastVisitForLevel.push(visit)
      }
    })
    return sortedVisits
  })

  return visitsWithLevels
}

function getRoomFilter(searchText, programRoomsVisible) {
  const defaultRoomFilter = room =>
    room.isVisible &&
    (programRoomsVisible
      ? room.isProgramAccommodation
      : !room.isProgramAccommodation)

  if (searchText === '') {
    return defaultRoomFilter
  } else {
    return room =>
      defaultRoomFilter(room) &&
      (room.name.toLowerCase().indexOf(searchText) !== -1 ||
        _.some(
          room.events,
          event =>
            event.guest &&
            event.guest.name.toLowerCase().indexOf(searchText) !== -1
        ))
  }
}

export const eventsByRoomSelector = createSelector(
  roomsSelector,
  visitsSelector,
  guestsSelector,
  dashboardStartDateSelector,
  dashboardEndDateSelector,
  getEventsByRoom
)

export const filteredRoomsSelector = createSelector(
  roomsSelector,
  eventsByRoomSelector,
  searchTextSelector,
  programRoomsVisibleSelector,
  (rooms, eventsByRoom, searchText, programRoomsVisible) => {
    let roomFilter = getRoomFilter(searchText, programRoomsVisible)

    return _(rooms)
      .values()
      .map(room => {
        return {
          ...room,
          events: eventsByRoom[room.id] || []
        }
      })
      .filter(roomFilter)
      .sortBy('name')
      .value()
  }
)



// WEBPACK FOOTER //
// ./src/redux/selectors/dashboardSelectors.js